import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import {
  CheckCircle, Circle, AlertTriangle, User, Calendar, Heart,
  Lightbulb, Shield, Phone, ArrowRight, Brain, MessageCircle
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverPreCall = () => {
  const navigate = useNavigate();
  const { childId, sessionId } = useParams();
  
  const [checklistData, setChecklistData] = useState(null);
  const [completedItems, setCompletedItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const getToken = () => localStorage.getItem('observer_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadChecklist();
  }, [navigate, childId]);

  const loadChecklist = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${BACKEND_URL}/api/observer/precall/checklist`, {
        params: { token, child_id: childId }
      });
      setChecklistData(response.data);
    } catch (error) {
      console.error('Error loading checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemId) => {
    setCompletedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const canStartSession = () => {
    if (!checklistData) return false;
    const requiredItems = checklistData.checklist.filter(item => item.required).map(item => item.id);
    return requiredItems.every(id => completedItems.includes(id));
  };

  const submitChecklist = async () => {
    setSubmitting(true);
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/observer/precall/complete`, null, {
        params: {
          token,
          child_id: childId,
          session_id: sessionId || `session_${Date.now()}`,
          notes
        },
        paramsSerializer: params => {
          const searchParams = new URLSearchParams(params);
          completedItems.forEach(item => searchParams.append('completed_items', item));
          return searchParams.toString();
        }
      });
      
      // Navigate to call screen
      navigate(`/observer/call/${childId}`);
    } catch (error) {
      console.error('Error submitting checklist:', error);
      alert(error.response?.data?.detail || 'Failed to submit checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const getMoodEmoji = (mood) => {
    const emojis = {
      very_happy: '😄',
      happy: '😊',
      neutral: '😐',
      sad: '😔',
      very_sad: '😢'
    };
    return emojis[mood] || '😐';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100" data-testid="precall-page">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Pre-Call Preparation</h1>
            <p className="text-gray-600">Complete this checklist before starting your session</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Checklist */}
            <div className="space-y-6">
              {/* Child Info Card */}
              {checklistData?.child && (
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white text-2xl font-bold">
                        {checklistData.child.name?.charAt(0)}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{checklistData.child.name}</h3>
                        <p className="text-gray-500">Age {checklistData.child.age}</p>
                        {checklistData.child.interests?.length > 0 && (
                          <p className="text-sm text-green-600">
                            Interests: {checklistData.child.interests.slice(0, 3).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Checklist */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Preparation Checklist
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {checklistData?.checklist?.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => toggleItem(item.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        completedItems.includes(item.id)
                          ? 'bg-green-50 border border-green-200'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      data-testid={`checklist-item-${item.id}`}
                    >
                      {completedItems.includes(item.id) ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                      <span className={completedItems.includes(item.id) ? 'text-green-800' : 'text-gray-700'}>
                        {item.label}
                      </span>
                      {item.required && (
                        <span className="text-xs text-red-500 ml-auto">Required</span>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Pre-call Notes (Optional)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any notes or reminders for this session..."
                    rows={3}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Context */}
            <div className="space-y-6">
              {/* Last Session */}
              {checklistData?.last_session?.date && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      Last Session
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700">
                      <strong>Date:</strong> {new Date(checklistData.last_session.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                    {checklistData.last_session.highlights && (
                      <p className="text-gray-600 mt-2">
                        <strong>Highlight:</strong> {checklistData.last_session.highlights}
                      </p>
                    )}
                    {checklistData.last_session.topics?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {checklistData.last_session.topics.map((topic, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                            {topic}
                          </span>
                        ))}
                      </div>
                    )}
                    {checklistData.last_session.mood_rating && (
                      <p className="mt-2 text-gray-600">
                        <strong>Mood:</strong> {checklistData.last_session.mood_rating}/5
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Weekly Question */}
              {checklistData?.weekly_question && (
                <Card className="border-l-4 border-l-purple-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <MessageCircle className="w-5 h-5 text-purple-500" />
                      This Week's Question
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-purple-800 font-medium italic">
                      "{checklistData.weekly_question}"
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Remember to ask this during your session!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Recent Moods */}
              {checklistData?.recent_moods?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Heart className="w-5 h-5 text-pink-500" />
                      Recent Mood History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2 flex-wrap">
                      {checklistData.recent_moods.map((mood, idx) => (
                        <div key={idx} className="text-center p-2 bg-gray-50 rounded-lg">
                          <span className="text-2xl">{getMoodEmoji(mood.mood)}</span>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(mood.logged_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Open Flags Warning */}
              {checklistData?.open_flags?.length > 0 && (
                <Card className="border-l-4 border-l-red-500 bg-red-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base text-red-700">
                      <Shield className="w-5 h-5" />
                      Active Safety Flags
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {checklistData.open_flags.map((flag, idx) => (
                        <div key={idx} className="p-2 bg-white rounded border border-red-200">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            flag.flag_level === 'red' ? 'bg-red-100 text-red-700' :
                            flag.flag_level === 'orange' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {flag.flag_level?.toUpperCase()}
                          </span>
                          {flag.notes && (
                            <p className="text-sm text-gray-600 mt-1">{flag.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-red-600 mt-3">
                      Please be extra attentive during this session.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Tips */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Session Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {checklistData?.tips?.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-yellow-500 mt-0.5">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Start Session Button */}
          <div className="mt-8 text-center">
            <Button
              size="lg"
              onClick={submitChecklist}
              disabled={!canStartSession() || submitting}
              className={`px-8 py-6 text-lg ${
                canStartSession() 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
              data-testid="start-session-btn"
            >
              {submitting ? (
                'Starting...'
              ) : (
                <>
                  <Phone className="w-5 h-5 mr-2" />
                  Start Session
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            {!canStartSession() && (
              <p className="text-sm text-red-500 mt-2">
                Please complete all required checklist items
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObserverPreCall;
